import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import CloseIconButton from 'Src/components/buttons/CloseIconButton';
import Copy from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';
import { Flex } from 'Src/components/layout/Flex';
import TitleItems from 'Src/components/TitleItems';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { UsedModalSearchParamControl } from 'Src/feature/modal/useModalControl';
import { usePaymentMethod } from 'Src/feature/payment/usePaymentMethod';
import { useOnErrorShowToast } from 'Src/feature/toasts/useOnErrorShowToast';

import { PayInOutTx } from './payInOutService';
import { usePayInOut } from './usePayInOut';

import styles from './PayInOutVoidConfirmation.module.scss';

const sectionId = 'PayInOutVoidConfirmation';

interface PayInOutVoidConfirmationProps {
  payInOutDetailModal: UsedModalSearchParamControl;
  payInOutVoidConfirmModal: UsedModalSearchParamControl;
  payInOutTx: PayInOutTx;
}

const PayInOutVoidConfirmation: FC<PayInOutVoidConfirmationProps> = ({ payInOutDetailModal, payInOutVoidConfirmModal, payInOutTx }) => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();

  const { data: paymentMethodMap } = usePaymentMethod.query.map();
  const { data: payInOutReasonMap } = usePayInOut.query.reason.map();

  const paymentMethod = paymentMethodMap?.get(payInOutTx.paymentMethodId);
  const payInOutReason = payInOutReasonMap?.get(payInOutTx.payInOutReasonId);

  const voidPayInOut = usePayInOut.mutate.void(useOnErrorShowToast({ title: t('payInOut.errors.void') }));

  const onClose = () => {
    payInOutVoidConfirmModal.close();
    payInOutDetailModal.close();
  };

  const onVoid = async () => {
    const response = await voidPayInOut.mutateAsync({ transactionId: payInOutTx.id as number });
    log.info('voidRequest()', { response });
    onClose();
  };

  return (
    <>
      <Modal
        data-testid={sectionId}
        open={payInOutVoidConfirmModal.isOpen}
        onBackDropClick={onClose}
      >
        <ModalHeader closeButton={<CloseIconButton onClick={onClose}/>}>
          <ModalTitle copy={t('labels.voidConfirmation')} />
        </ModalHeader>

        <ModalContent>
          <Flex>
            <Copy value={payInOutTx.isPayIn ? t('payInOut.labels.confirmPayInVoid') : t('payInOut.labels.confirmPayOutVoid')} align="center" />
            <TitleItems
              items={[
                { primary: t('labels.time'), secondary: <Copy value={payInOutTx.createdAt ? DateTime.fromMillis(payInOutTx.createdAt)?.toLocaleString(DateTime.TIME_SIMPLE) : ''} /> },
                { primary: t('labels.type'), secondary: <Copy value={payInOutTx.isPayIn ? t('payInOut.labels.payIn') : t('payInOut.labels.payOut')} /> },
                { primary: t('labels.amount'), secondary: <Copy type="currency" value={payInOutTx.amount} /> },
                { primary: t('labels.method'), secondary: <Copy value={paymentMethod?.name} /> },
                { primary: t('labels.reason'), secondary: <Copy value={payInOutReason?.name} /> },
              ]}
            />
            <Flex horizontal gap="xs" alignItems="center" justifyContent="center" className={styles['warning']}>
              <FontIcon name="error_outline" />
              <Copy value={t('payInOut.labels.notReversible')} />
            </Flex>
          </Flex>
        </ModalContent>

        <ModalActions>
          <ModalAction
            data-testid={`${sectionId}-cancel-btn`}
            onClick={onClose}
            color="secondary"
            label={t('labels.cancel')}
          />
          <ModalAction
            data-testid={`${sectionId}-void-btn`}
            onClick={() => {
              void onVoid();
            }}
            color="primary"
            label={payInOutTx.isPayIn ? t('payInOut.labels.voidPayIn') : t('payInOut.labels.voidPayOut')}
          />
        </ModalActions>
      </Modal>
    </>
  );
};

export { PayInOutVoidConfirmation, sectionId };
